import type { ReactNode } from "react"

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

export type BuilderTabItem = {
  value: string
  label: string
  content: ReactNode
}

type BuilderTabsProps = {
  tabs: BuilderTabItem[]
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  searchSlot?: ReactNode
  className?: string
  contentClassName?: string
}

const tabsTriggerClassName =
  "data-[state=active]:bg-background rounded border-0 px-4 shadow-none data-[state=active]:border-0 data-[state=active]:shadow-sm"

/** Reusable tab strip for builder library views (Flows, Dash). */
export function BuilderTabs({
  tabs,
  defaultValue,
  value,
  onValueChange,
  searchSlot,
  className,
  contentClassName,
}: BuilderTabsProps) {
  const initial = defaultValue ?? tabs[0]?.value
  const isControlled = value !== undefined

  return (
    <Tabs
      className={cn("flex min-h-0 flex-1 flex-col", className)}
      {...(isControlled ? { value, onValueChange } : { defaultValue: initial })}
    >
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <TabsList className="bg-muted h-10 rounded-md border-0 p-1">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} className={tabsTriggerClassName} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {searchSlot}
      </div>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          className={cn("mt-0 min-h-0 flex-1 data-[state=inactive]:hidden", contentClassName)}
          value={tab.value}
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  )
}
